import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { serviceTypesAPI, servicesAPI, subServicesAPI } from '../../utils/api';

const AdminServices = () => {
  const navigate = useNavigate();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [expandedServices, setExpandedServices] = useState({});
  const [activeContentTab, setActiveContentTab] = useState('information');
  
  // Dialog states
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [subServiceDialogOpen, setSubServiceDialogOpen] = useState(false);
  
  const [editingType, setEditingType] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingSubService, setEditingSubService] = useState(null);
  
  const [typeForm, setTypeForm] = useState({ name: '', description: '', icon: '' });

  const EMPTY_CONTENT = {
    information: '',
    how_to_apply: '',
    documents: '',
    faqs: '',
  };
  
  const [serviceForm, setServiceForm] = useState({
    service_type_id: '',
    name: '',
    description: '',
    processing_time: '3-5 business days',
    content: { ...EMPTY_CONTENT },
  });

  const [subServiceForm, setSubServiceForm] = useState({ service_id: '', name: '', description: '' });

  const editorModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
      
    ],
  };
  

  const editorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
];


  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [typesRes, servicesRes, subServicesRes] = await Promise.all([
        serviceTypesAPI.getAll(true),
        servicesAPI.getAll(null, true),
        subServicesAPI.getAll(null, true)
      ]);
      setServiceTypes(typesRes.data);
      setServices(servicesRes.data);
      setSubServices(subServicesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Service Type handlers
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await serviceTypesAPI.update(editingType.id, typeForm);
        toast.success('Service type updated');
      } else {
        await serviceTypesAPI.create(typeForm);
        toast.success('Service type created');
      }
      setTypeDialogOpen(false);
      setEditingType(null);
      setTypeForm({ name: '', description: '', icon: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeForm({ name: type.name, description: type.description || '', icon: type.icon || '' });
    setTypeDialogOpen(true);
  };

  const handleDeleteType = async (id) => {
    if (window.confirm('Delete this service type? This will also affect related services.')) {
      try {
        await serviceTypesAPI.delete(id);
        toast.success('Service type deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  // Service handlers
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await servicesAPI.update(editingService.id, serviceForm);
        toast.success('Service updated');
      } else {
        await servicesAPI.create(serviceForm);
        toast.success('Service created');
      }
      setServiceDialogOpen(false);
      setEditingService(null);
      setServiceForm({ service_type_id: '', name: '', description: '', processing_time: '3-5 business days' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);

    setServiceForm({
      service_type_id: service.service_type_id,
      name: service.name,
      description: service.description || '',
      processing_time: service.processing_time || '3-5 business days',
      content: {
        ...EMPTY_CONTENT,
        ...(service.content || {}),
      },
    });

    setActiveContentTab('information');
    setServiceDialogOpen(true);
  };

  const handleDeleteService = async (id) => {
    if (window.confirm('Delete this service?')) {
      try {
        await servicesAPI.delete(id);
        toast.success('Service deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  // Sub-Service handlers
  const handleSubServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubService) {
        await subServicesAPI.update(editingSubService.id, subServiceForm);
        toast.success('Sub-service updated');
      } else {
        await subServicesAPI.create(subServiceForm);
        toast.success('Sub-service created');
      }
      setSubServiceDialogOpen(false);
      setEditingSubService(null);
      setSubServiceForm({ service_id: '', name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditSubService = (subService) => {
    setEditingSubService(subService);
    setSubServiceForm({
      service_id: subService.service_id,
      name: subService.name,
      description: subService.description || ''
    });
    setSubServiceDialogOpen(true);
  };

  const handleDeleteSubService = async (id) => {
    if (window.confirm('Delete this sub-service?')) {
      try {
        await subServicesAPI.delete(id);
        toast.success('Sub-service deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const toggleType = (typeId) => {
    setExpandedTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  const toggleService = (serviceId) => {
    setExpandedServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Services Management
              </h1>
              <p className="text-muted-foreground">
                Manage service types, services, and sub-services
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>

          <Tabs defaultValue="hierarchy" className="space-y-6">
            <TabsList>
              <TabsTrigger value="hierarchy">Service Hierarchy</TabsTrigger>
              <TabsTrigger value="types">Service Types</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="subservices">Sub-Services</TabsTrigger>
            </TabsList>

            <TabsContent value="hierarchy" className="space-y-4">
              <div className="flex gap-2 mb-4">
                <Button onClick={() => { setEditingType(null); setTypeForm({ name: '', description: '', icon: '' }); setTypeDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Type
                </Button>
                <Button variant="outline" onClick={() => { setEditingService(null); setServiceForm({ service_type_id: '', name: '', description: '', processing_time: '3-5 business days' }); setServiceDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Service
                </Button>
                <Button variant="outline" onClick={() => { setEditingSubService(null); setSubServiceForm({ service_id: '', name: '', description: '' }); setSubServiceDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Sub-Service
                </Button>
              </div>

              <div className="bg-white rounded-lg border shadow-sm">
                {serviceTypes.map((type) => (
                  <div key={type.id} className={`border-b last:border-b-0 ${!type.is_active ? 'opacity-50' : ''}`}>
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleType(type.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedTypes[type.id] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        <Layers className="h-5 w-5 text-accent" />
                        <span className="font-semibold">{type.name}</span>
                        <span className="text-sm text-muted-foreground">({services.filter(s => s.service_type_id === type.id).length} services)</span>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => handleEditType(type)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteType(type.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    {expandedTypes[type.id] && (
                      <div className="pl-12 pb-4">
                        {services.filter(s => s.service_type_id === type.id).map((service) => (
                          <div key={service.id} className={`border-l-2 border-slate-200 ${!service.is_active ? 'opacity-50' : ''}`}>
                            <div
                              className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                              onClick={() => toggleService(service.id)}
                            >
                              <div className="flex items-center gap-2">
                                {expandedServices[service.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <span>{service.name}</span>
                                <span className="text-xs text-muted-foreground">({subServices.filter(ss => ss.service_id === service.id).length} sub-services)</span>
                              </div>
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" variant="ghost" onClick={() => handleEditService(service)}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteService(service.id)}>
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                            
                            {expandedServices[service.id] && (
                              <div className="pl-8 pb-2">
                                {subServices.filter(ss => ss.service_id === service.id).map((subService) => (
                                  <div key={subService.id} className={`flex items-center justify-between p-2 border-l-2 border-slate-100 hover:bg-slate-50 ${!subService.is_active ? 'opacity-50' : ''}`}>
                                    <span className="text-sm">{subService.name}</span>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="ghost" onClick={() => handleEditSubService(subService)}>
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => handleDeleteSubService(subService.id)}>
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                {subServices.filter(ss => ss.service_id === service.id).length === 0 && (
                                  <p className="text-sm text-muted-foreground p-2">No sub-services</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        {services.filter(s => s.service_type_id === type.id).length === 0 && (
                          <p className="text-sm text-muted-foreground p-3">No services in this type</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="types">
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Service Types</h3>
                  <Button onClick={() => { setEditingType(null); setTypeForm({ name: '', description: '', icon: '' }); setTypeDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Type
                  </Button>
                </div>
                <div className="space-y-2">
                  {serviceTypes.map((type) => (
                    <div key={type.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{type.name}</span>
                        {type.description && <p className="text-sm text-muted-foreground">{type.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEditType(type)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteType(type.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="services">
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Services</h3>
                  <Button onClick={() => { setEditingService(null); setServiceForm({ service_type_id: '', name: '', description: '', processing_time: '3-5 business days' }); setServiceDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Service
                  </Button>
                </div>
                <div className="space-y-2">
                  {services.map((service) => {
                    const type = serviceTypes.find(t => t.id === service.service_type_id);
                    return (
                      <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-medium">{service.name}</span>
                          <p className="text-sm text-muted-foreground">Type: {type?.name || 'Unknown'} | Processing: {service.processing_time}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEditService(service)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteService(service.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="subservices">
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Sub-Services</h3>
                  <Button onClick={() => { setEditingSubService(null); setSubServiceForm({ service_id: '', name: '', description: '' }); setSubServiceDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Sub-Service
                  </Button>
                </div>
                <div className="space-y-2">
                  {subServices.map((subService) => {
                    const service = services.find(s => s.id === subService.service_id);
                    return (
                      <div key={subService.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-medium">{subService.name}</span>
                          <p className="text-sm text-muted-foreground">Service: {service?.name || 'Unknown'}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEditSubService(subService)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteSubService(subService.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Service Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Service Type' : 'Add Service Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTypeSubmit} className="space-y-4">
            <div>
              <Label htmlFor="type-name">Name *</Label>
              <Input
                id="type-name"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="type-desc">Description</Label>
              <Textarea
                id="type-desc"
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingType ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SERVICE DIALOG */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingService ? 'Edit Service' : 'Add Service'}
            </DialogTitle>
          </DialogHeader>

          {/* SCROLLABLE BODY */}
          <div className="overflow-y-auto pr-2 max-h-[75vh]">
            <form onSubmit={handleServiceSubmit} className="space-y-6">

              {/* SERVICE TYPE */}
              <div>
                <Label>Service Type *</Label>
                <Select
                  value={serviceForm.service_type_id}
                  onValueChange={(value) =>
                    setServiceForm({ ...serviceForm, service_type_id: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.filter(t => t.is_active).map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* NAME */}
              <div>
                <Label>Name *</Label>
                <Input
                  value={serviceForm.name}
                  onChange={e =>
                    setServiceForm({ ...serviceForm, name: e.target.value })
                  }
                  required
                />
              </div>

              {/* SHORT DESCRIPTION */}
              <div>
                <Label>Short Description</Label>
                <Textarea
                  rows={3}
                  value={serviceForm.description}
                  onChange={e =>
                    setServiceForm({ ...serviceForm, description: e.target.value })
                  }
                />
              </div>

              {/* PROCESSING TIME */}
              <div>
                <Label>Processing Time</Label>
                <Input
                  value={serviceForm.processing_time}
                  onChange={e =>
                    setServiceForm({ ...serviceForm, processing_time: e.target.value })
                  }
                />
              </div>

              {/* CMS CONTENT */}
              <div>
                <Label className="mb-2 block">Service Content</Label>

                <Tabs
                  value={activeContentTab}
                  onValueChange={setActiveContentTab}
                  className="w-full"
                >
                  <TabsList className="mb-4">
                    <TabsTrigger value="information">Information</TabsTrigger>
                    <TabsTrigger value="how_to_apply">How To Apply</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                    <TabsTrigger value="faqs">FAQs</TabsTrigger>
                  </TabsList>

                  {/* INFORMATION */}
                  <TabsContent value="information">
                    <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        modules={editorModules}
                        formats={editorFormats}
                        value={serviceForm.content?.information || ''}
                        onChange={value =>
                          setServiceForm({
                            ...serviceForm,
                            content: { ...serviceForm.content, information: value },
                          })
                        }
                        className="min-h-[240px]"
                      />
                    </div>
                  </TabsContent>

                  {/* HOW TO APPLY */}
                  <TabsContent value="how_to_apply">
                    <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        modules={editorModules}
                        formats={editorFormats}
                        value={serviceForm.content?.how_to_apply || ''}
                        onChange={value =>
                          setServiceForm({
                            ...serviceForm,
                            content: { ...serviceForm.content, how_to_apply: value },
                          })
                        }
                        className="min-h-[240px]"
                      />
                    </div>
                  </TabsContent>

                  {/* DOCUMENTS */}
                  <TabsContent value="documents">
                    <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        modules={editorModules}
                        formats={editorFormats}
                        value={serviceForm.content?.documents || ''}
                        onChange={value =>
                          setServiceForm({
                            ...serviceForm,
                            content: { ...serviceForm.content, documents: value },
                          })
                        }
                        className="min-h-[240px]"
                      />
                    </div>
                  </TabsContent>

                  {/* FAQS */}
                  <TabsContent value="faqs">
                    <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        modules={editorModules}
                        formats={editorFormats}
                        value={serviceForm.content?.faqs || ''}
                        onChange={value =>
                          setServiceForm({
                            ...serviceForm,
                            content: { ...serviceForm.content, faqs: value },
                          })
                        }
                        className="min-h-[240px]"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* ACTIONS */}
              <DialogFooter className="pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setServiceDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingService ? 'Update Service' : 'Create Service'}
                </Button>
              </DialogFooter>

            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sub-Service Dialog */}
      <Dialog open={subServiceDialogOpen} onOpenChange={setSubServiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubService ? 'Edit Sub-Service' : 'Add Sub-Service'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubServiceSubmit} className="space-y-4">
            <div>
              <Label>Parent Service *</Label>
              <Select
                value={subServiceForm.service_id}
                onValueChange={(value) => setSubServiceForm({ ...subServiceForm, service_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter(s => s.is_active).map((service) => (
                    <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subservice-name">Name *</Label>
              <Input
                id="subservice-name"
                value={subServiceForm.name}
                onChange={(e) => setSubServiceForm({ ...subServiceForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="subservice-desc">Description</Label>
              <Textarea
                id="subservice-desc"
                value={subServiceForm.description}
                onChange={(e) => setSubServiceForm({ ...subServiceForm, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubServiceDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingSubService ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
